import { type } from 'arktype';

/**
 * Organisation structure models (F20 — org-chart-position-editor, Phase 2a).
 *
 * Request schemas are hand-written (manual fallback of the codegen-first
 * workflow): versioned writes (splicing semantics, `validFrom` key dates,
 * confirm-gate passthrough fields) cannot be expressed by the current
 * `crud-resource` manifest schema — documented in the story file
 * `docs/sprint-artifacts/week26/stories/f20-2-org-api.md`.
 */

export const dateKey = type(/^\d{4}-\d{2}-\d{2}$/)
  .narrow((value, ctx) => {
    // Shape alone lets '2026-02-30' through — downstream date arithmetic
    // (previousDayKey) and the Postgres date cast would turn that into a 500.
    const parsed = new Date(`${value}T00:00:00Z`);
    return (!Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value) || ctx.mustBe('a valid calendar date');
  })
  .configure({
    description: 'ISO date key (YYYY-MM-DD).',
    examples: ['2026-07-08'],
  });

const uuidString = type('string.uuid').configure({ description: 'UUID.' });
const nullableUuidString = uuidString.or(type('null'));

export const orgUnitKindSchema = type("'organisation' | 'unit' | 'location'").configure({
  description: 'Display kind of an org-unit node (icon/label only, no behavioural semantics).',
});

export type OrgUnitKind = typeof orgUnitKindSchema.t;

/**
 * Past-impact confirm-gate passthrough fields (spec Story 4). Accepted on
 * every org write endpoint from Phase 2a on; the actual gate logic (preview,
 * hash token, four-eyes 403) lands in Phase 2b behind the `PastImpactGate`
 * hook.
 */
export const confirmGateFields = {
  'confirmPastImpact?': type('boolean').configure({
    description: 'Explicit confirmation that the write may change the effective state of past days.',
  }),
  'confirmedPreviewHash?': type('string').configure({
    description: 'Hash of the consequence preview the confirmer has seen (optimistic-concurrency token).',
  }),
} as const;

// ---------------------------------------------------------------------------
// Org units
// ---------------------------------------------------------------------------

export const createOrgUnitSchema = type({
  name: type('string > 0').configure({ description: 'Display name of the org unit.', examples: ['Restaurant Bern'] }),
  'parentId?': nullableUuidString.configure({ description: 'Parent org-unit id; null or omitted = root node.' }),
  'kind?': orgUnitKindSchema,
  'departmentId?': type('string | null').configure({
    description: 'Department this org unit belongs to (text id from the departments catalogue).',
  }),
  'sortOrder?': type('number.integer').configure({ description: 'Sibling ordering inside the parent node.' }),
  validFrom: dateKey.configure({ description: 'First day (inclusive) the new unit exists in the tree.' }),
  ...confirmGateFields,
}).configure({ description: 'Payload to create an org unit (master row + first version row).' });

export type CreateOrgUnit = typeof createOrgUnitSchema.t;

export const updateOrgUnitSchema = type({
  'name?': type('string > 0').configure({ description: 'New display name.' }),
  'parentId?': nullableUuidString.configure({ description: 'New parent org-unit id; null = make root.' }),
  'kind?': orgUnitKindSchema,
  'departmentId?': type('string | null').configure({ description: 'New department id; null = unassign department.' }),
  'sortOrder?': type('number.integer').configure({ description: 'New sibling ordering.' }),
  validFrom: dateKey.configure({ description: 'Key date (inclusive) the change becomes effective — splices the version timeline.' }),
  ...confirmGateFields,
}).configure({ description: 'Versioned partial update of an org unit, effective from `validFrom`.' });

export type UpdateOrgUnit = typeof updateOrgUnitSchema.t;

// ---------------------------------------------------------------------------
// Positions
// ---------------------------------------------------------------------------

export const createPositionSchema = type({
  name: type('string > 0').configure({ description: 'Display name of the position.', examples: ['Koch – Restaurant Bern'] }),
  orgUnitId: uuidString.configure({ description: 'Org unit the position belongs to.' }),
  jobRoleId: type('string > 0').configure({ description: 'Job-role catalogue id (text id, not uuid).' }),
  validFrom: dateKey.configure({ description: 'First day (inclusive) the position exists.' }),
  ...confirmGateFields,
}).configure({ description: 'Payload to create a position (master row + first version row).' });

export type CreatePosition = typeof createPositionSchema.t;

export const updatePositionSchema = type({
  'name?': type('string > 0').configure({ description: 'New display name.' }),
  'orgUnitId?': uuidString.configure({ description: 'New owning org unit (re-parent).' }),
  'jobRoleId?': type('string > 0').configure({ description: 'New job-role catalogue id.' }),
  validFrom: dateKey.configure({ description: 'Key date (inclusive) the change becomes effective — splices the version timeline.' }),
  ...confirmGateFields,
}).configure({ description: 'Versioned partial update of a position, effective from `validFrom`.' });

export type UpdatePosition = typeof updatePositionSchema.t;

// ---------------------------------------------------------------------------
// Archive (org units + positions)
// ---------------------------------------------------------------------------

export const archiveOrgNodeSchema = type({
  validFrom: dateKey.configure({ description: 'First day (inclusive) the node no longer exists in the tree.' }),
  ...confirmGateFields,
}).configure({ description: 'Archive an org unit or position effective from a key date. Blocked (409) while dependents exist.' });

export type ArchiveOrgNode = typeof archiveOrgNodeSchema.t;

// ---------------------------------------------------------------------------
// Person ↔ position assignments
// ---------------------------------------------------------------------------

export const createPersonPositionSchema = type({
  personId: uuidString.configure({ description: 'Person being assigned.' }),
  positionId: uuidString.configure({ description: 'Target position.' }),
  validFrom: dateKey.configure({ description: 'First day (inclusive) of the assignment.' }),
  'mode?': type("'assign' | 'transfer' | 'parallel'").configure({
    description:
      "'assign' (default): plain new assignment. 'transfer': close all other open assignments of the person the day before. 'parallel': deliberate additional position (kept open alongside).",
  }),
  ...confirmGateFields,
}).configure({ description: 'Assign a person to a position (plain, transfer, or parallel), effective-dated.' });

export type CreatePersonPosition = typeof createPersonPositionSchema.t;

export const endPersonPositionSchema = type({
  endOn: dateKey.configure({ description: 'Last day (inclusive) of the assignment — the row is closed, never deleted.' }),
  ...confirmGateFields,
}).configure({ description: 'End a person-position assignment at a key date.' });

export type EndPersonPosition = typeof endPersonPositionSchema.t;

// ---------------------------------------------------------------------------
// Direct role assignments to org-chart nodes (spec Story 2 — F20 Phase 3a)
// ---------------------------------------------------------------------------

export const createOrgRoleAssignmentSchema = type({
  roleId: type('string > 0').configure({ description: 'Semantic role id (rbac.roles); must be flagged assignableToPositions.' }),
  'orgUnitId?': nullableUuidString.configure({ description: 'Target org unit (inherited by the subtree); XOR with positionId.' }),
  'positionId?': nullableUuidString.configure({ description: 'Target position (fine-grained); XOR with orgUnitId.' }),
  validFrom: dateKey.configure({ description: 'First day (inclusive) the role assignment applies.' }),
  ...confirmGateFields,
}).configure({ description: 'Attach a curated role to exactly one org unit XOR position, effective-dated.' });

export type CreateOrgRoleAssignment = typeof createOrgRoleAssignmentSchema.t;

export const endOrgRoleAssignmentSchema = type({
  endOn: dateKey.configure({ description: 'Last day (inclusive) of the role assignment — the row is closed, never deleted.' }),
  ...confirmGateFields,
}).configure({ description: 'End a direct role assignment at a key date (append-only).' });

export type EndOrgRoleAssignment = typeof endOrgRoleAssignmentSchema.t;

export const orgRoleAssignmentListQuerySchema = type({
  'orgUnitId?': uuidString.configure({ description: 'Filter assignments by target org unit.' }),
  'positionId?': uuidString.configure({ description: 'Filter assignments by target position.' }),
  'roleId?': type('string > 0').configure({ description: 'Filter assignments by role id.' }),
}).configure({ description: 'Query parameters for the direct role-assignment history.' });

export type OrgRoleAssignmentListQuery = typeof orgRoleAssignmentListQuerySchema.t;

/** Effective-dated direct role assignment to an org unit XOR position. */
export interface OrgRoleAssignmentDto {
  id: string;
  roleId: string;
  orgUnitId: string | null;
  positionId: string | null;
  validFrom: string;
  validTo: string | null;
}

// ---------------------------------------------------------------------------
// Consequence preview (spec Story 4 — Vergangenheits-Schutz)
// ---------------------------------------------------------------------------

const nullableDateKey = dateKey.or(type('null'));

export const orgChangePreviewRequestSchema = type({
  kind: type("'orgUnit' | 'position' | 'personPosition' | 'roleAssignment' | 'bundleAssignment'").configure({
    description: 'Entity kind of the planned write.',
  }),
  action: type("'create' | 'update' | 'archive' | 'assign' | 'transfer' | 'parallel' | 'end'").configure({
    description: 'Planned write action.',
  }),
  'entityId?': nullableUuidString.configure({ description: 'Stable entity id (org unit / position / assignment); null for creates.' }),
  effectiveFrom: dateKey.configure({
    description: 'Key date of the planned write (validFrom, or endOn for person-position ends).',
  }),
  'effectiveTo?': nullableDateKey.configure({
    description: 'Current validTo of the row a person-position end targets; null/omitted = open-ended.',
  }),
  'personId?': nullableUuidString.configure({ description: 'Subject person of a person-position write.' }),
  'positionId?': nullableUuidString.configure({
    description: 'Subject position of a person-position write, or the target position of a role/bundle assignment.',
  }),
  'orgUnitId?': nullableUuidString.configure({ description: 'Target org unit of a role/bundle assignment write.' }),
}).configure({ description: 'Describes a PLANNED org change for a consequence preview — nothing is written.' });

export type OrgChangePreviewRequest = typeof orgChangePreviewRequestSchema.t;

/** Classification of an org write over the EFFECTIVE state (spec Story 4). */
export type OrgChangeClassification = 'futureOnly' | 'pastAffecting';

/** Compact affected-person entry of a consequence preview (capped list). */
export interface OrgChangeAffectedPerson {
  id: string;
  name: string;
}

/**
 * Consequence preview of a past-affecting org change (spec Story 4).
 * Deliberately its OWN type — not the workload `ConsequencePreview` of
 * `@zag/timetracking/api` (different domain, different shape).
 *
 * Returned by `POST core/organisation/consequence-preview` and as the payload
 * of the 409 the confirm gate throws for unconfirmed past-affecting writes.
 */
export interface OrgChangeConsequencePreview {
  classification: OrgChangeClassification;
  /** First day (inclusive) whose as-of resolution changes. */
  affectedFrom: string;
  /** Last affected day (inclusive); null = open-ended. */
  affectedTo: string | null;
  /** Past portion (< server today) of the affected window; null when futureOnly. */
  pastFrom: string | null;
  pastTo: string | null;
  /** TOTAL number of affected persons (the `affectedPersons` list is capped). */
  affectedPersonCount: number;
  affectedPersons: OrgChangeAffectedPerson[];
  /** Distinct booked person-days (`bookings.booking_records`, status=active) in the past window. */
  bookedDayCount: number;
  /** Subset of `bookedDayCount` with reviewStatus=approved (confirmed days). */
  confirmedDayCount: number;
  /** Verdicts (`timetracking.verdicts`) evaluated for affected persons in the past window. */
  affectedVerdictCount: number;
  /**
   * `true` when `ExpectedHoursService` will lazily void/re-post
   * `expected_workday` bookings on the next read — balances change WITHOUT a
   * further write (the change itself rewrites nothing, default forward-only).
   */
  expectedHoursLazyRewrite: boolean;
  /** Optimistic-concurrency token: sha256 over the canonicalised preview (no timestamps). */
  previewHash: string;
}

export const personPositionListQuerySchema = type({
  'personId?': uuidString.configure({ description: 'Filter assignments by person id.' }),
  'positionId?': uuidString.configure({ description: 'Filter assignments by position id.' }),
}).configure({ description: 'Query parameters for the person-position assignment history.' });

export type PersonPositionListQuery = typeof personPositionListQuerySchema.t;

// ---------------------------------------------------------------------------
// Response DTOs
// ---------------------------------------------------------------------------

/** Effective-dated version row of an org unit. */
export interface OrgUnitVersionDto {
  id: string;
  orgUnitId: string;
  parentId: string | null;
  name: string;
  kind: OrgUnitKind;
  departmentId: string | null;
  sortOrder: number;
  validFrom: string;
  validTo: string | null;
}

/** Effective-dated version row of a position. */
export interface PositionVersionDto {
  id: string;
  positionId: string;
  orgUnitId: string;
  jobRoleId: string;
  name: string;
  validFrom: string;
  validTo: string | null;
}

/** Effective-dated person ↔ position assignment row. */
export interface PersonPositionDto {
  id: string;
  personId: string;
  positionId: string;
  validFrom: string;
  validTo: string | null;
}

/** Result of a versioned org-structure write. */
export interface OrgVersionWriteResult<TVersion> {
  /** Stable entity id (org unit / position). */
  entityId: string;
  /** The version row created by this write. */
  version: TVersion;
  /**
   * Number of later planned versions that stayed in place and cap the new
   * row — the editor shows a hint when > 0 (spec Story 1 splicing AC).
   */
  laterVersionCount: number;
}

export type OrgUnitWriteResult = OrgVersionWriteResult<OrgUnitVersionDto>;
export type PositionWriteResult = OrgVersionWriteResult<PositionVersionDto>;

/** Compact person preview on position nodes (avatar/initials rendering). */
export interface OrgTreePersonPreview {
  id: string;
  name: string;
  initials: string;
}

/**
 * Rule-bundle badge on a tree node (F20 Phase 3b, spec Story 3/5): directly
 * assigned on the node, or inherited from an ancestor org unit
 * (`sourceOrgUnitId` names the unit whose direct assignment is inherited).
 * Nearest source wins on duplicates; a direct assignment beats inherited.
 */
export interface OrgTreeBundleBadge {
  bundleId: string;
  name: string;
  /** Palette chip colour of the bundle (design token key or hex). */
  color: string | null;
  origin: 'direct' | 'inherited';
  /** Ancestor unit the badge is inherited from; null when direct. */
  sourceOrgUnitId: string | null;
}

/** Direct/inherited role badge on a tree node (same semantics as bundle badges). */
export interface OrgTreeRoleBadge {
  roleId: string;
  origin: 'direct' | 'inherited';
  /** Ancestor unit the badge is inherited from; null when direct. */
  sourceOrgUnitId: string | null;
}

/** Badge set of one tree node — as-of bundle and role assignments. */
export interface OrgTreeNodeBadges {
  bundles: OrgTreeBundleBadge[];
  roles: OrgTreeRoleBadge[];
}

/** One node of the as-of org tree (org unit or position). */
export interface OrgTreeNode {
  /** Stable entity id (org unit id or position id). */
  id: string;
  nodeType: 'orgUnit' | 'position';
  /** Display kind — org-unit nodes only, null for positions. */
  kind: OrgUnitKind | null;
  /** Department this org unit belongs to (org-unit nodes only, null for positions). */
  departmentId: string | null;
  /** Department display name (org-unit nodes only, null when no department). */
  departmentName: string | null;
  name: string;
  sortOrder: number;
  /** Position nodes: directly assigned persons. Org units: distinct persons in the whole subtree. */
  personCount: number;
  /** Directly assigned persons preview — position nodes only (capped). */
  persons: OrgTreePersonPreview[];
  /** As-of bundle/role badges, direct and inherited (F20 Phase 3b). */
  badges: OrgTreeNodeBadges;
  children: OrgTreeNode[];
}

/** Response of `GET core/organisation/tree?date=`. */
export interface OrgTreeResponse {
  /** The as-of key date the tree was resolved for. */
  date: string;
  roots: OrgTreeNode[];
}

/** One change date of the version timeline (PO feedback 2026-07-08, spec Story 5). */
export const orgChangeDateSchema = type({
  date: dateKey,
  changeCount: type('number.integer >= 1').configure({
    description: 'Number of effective-dated rows that begin on this date or end on the previous day.',
  }),
}).configure({
  description: 'A date on which the effective org state changes (structure versions, bundle/role assignments, person assignments).',
});
export type OrgChangeDate = typeof orgChangeDateSchema.t;

/**
 * Response of `GET core/organisation/change-dates` — the data source of the
 * editor's version timeline: sorted, distinct change dates derived from ALL
 * effective-dated writes (`valid_from` values plus `valid_to + 1 day` where
 * closed) across org-unit/position versions, person positions, direct role
 * assignments, and rule-bundle assignments.
 */
export const orgChangeDatesResponseSchema = type({
  dates: orgChangeDateSchema.array(),
}).configure({ description: 'Sorted (ascending), distinct list of org change dates.' });
export type OrgChangeDatesResponse = typeof orgChangeDatesResponseSchema.t;
